import React, { Fragment, PureComponent } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  Article,
  CreativeWork,
  Offer,
  Person,
  PostalAddress,
  Thing,
  WithContext
} from 'schema-dts';
import { BzitVehicleSearch } from '../../bzit';
import {
  withSearch,
  WithSearchProps
} from '../../provider/SearchProvider/withSearch';
import {
  selectMeta,
  selectVehicleCenters
} from '../../provider/Typo3Provider/selectors';
import { MagazinePage } from '../../provider/Typo3Provider/types';
import { getVehicleDetailPath } from '../../provider/Typo3Provider/utils';
import {
  withTypo3,
  WithTypo3Props
} from '../../provider/Typo3Provider/withTypo3';
import { logo } from '../../theme/images';

interface SchemaOrgProps {
  page: MagazinePage;
}

interface SchemaOrgState {
  doktype: string;
  offers: BzitVehicleSearch[];
}

class SchemaOrg extends PureComponent<
  SchemaOrgProps & RouteComponentProps & WithTypo3Props & WithSearchProps,
  SchemaOrgState
> {
  public state: SchemaOrgState = {
    doktype: 'default',
    offers: []
  };

  public async componentDidMount() {
    const { page, typo3 } = this.props;
    const meta = selectMeta(typo3);

    if (meta) {
      if (page.doktype) {
        const doktype =
          page.doktype === 51
            ? 'location'
            : page.doktype === 52
            ? 'magazine'
            : 'default';

        this.setState({
          doktype
        });

        if (doktype === 'location') {
          const vehicleCenters = selectVehicleCenters(typo3);
          const usedvehiclecenter = vehicleCenters.find(
            center => center.link === window.location.pathname
          );

          if (usedvehiclecenter) {
            const { search } = this.props;
            const offers = await search.getOffersByLocation(
              search.formParams,
              usedvehiclecenter.searchValue
            );

            this.setState({
              offers
            });
          }
        }
      }
    }
  }

  public JsonLd = <T extends Thing>(json: WithContext<T>): string => {
    return JSON.stringify(json);
  };

  public schema = <T extends Thing>(json: WithContext<T>): {} => {
    return json;
  };

  public formatDate = (date: number) => {
    const d = new Date(date * 1000);
    let month = (d.getMonth() + 1).toString();
    let day = d.getDate().toString();
    const year = d.getFullYear();

    month = month.length < 2 ? '0' + month : month;
    day = day.length < 2 ? '0' + day : day;

    return [year, month, day].join('-');
  };

  public render() {
    const { location, page, typo3 } = this.props;
    const { doktype } = this.state;
    const { protocol, host } = window.location;
    const origin = `${protocol}//${host}`;

    // location page
    if (doktype === 'location') {
      const vehicleCenters = selectVehicleCenters(typo3);
      const usedvehiclecenter = vehicleCenters.find(
        center => center.link === location.pathname
      );

      if (usedvehiclecenter) {
        const { offers } = this.state;
        const {
          image,
          name,
          company,
          countryCode: addressCountry,
          city: addressLocality,
          zip: postalCode,
          street: streetAddress,
          latitude,
          longitude,
          phone: telephone,
          email,
          employees
        } = usedvehiclecenter;
        type Day =
          | 'monday'
          | 'tuesday'
          | 'wednesday'
          | 'thursday'
          | 'friday'
          | 'saturday'
          | 'sunday';

        const days: Day[] = [
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
          'sunday'
        ];

        const address = this.schema<PostalAddress>({
          '@context': 'https://schema.org',
          '@type': 'PostalAddress',
          addressCountry,
          addressLocality,
          postalCode,
          streetAddress
        });

        const openingHoursSpecification = days
          .map((day) => {
            const { [day]: open } = usedvehiclecenter;
            if (!open) {
              return null;
            }

            const item = open.split('-');
            const opens = item[0].trim();
            const closes = item[1].trim();
            return {
              '@context': 'https://schema.org',
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: day,
              opens,
              closes
            };
          })
          .filter((entry) => entry)
        ;

        let employee = {};
        if (employees !== undefined) {
          employee = employees.map(employee => {
            const {
              name,
              email,
              fax: faxNumber,
              primary_phone,
              phone,
              mobile,
              position_label: jobTitle
            } = employee;
            const telephone = primary_phone === 0 ? phone : mobile || phone;
            return this.schema<Person>({
              '@context': 'https://schema.org',
              '@type': 'Person',
              name,
              email,
              telephone,
              faxNumber,
              jobTitle
            });
          });
        }

        const makesOffer = offers.reduce<unknown[]>((schemaOrgOffers, offer) => {
          if (schemaOrgOffers.length >= 3) {
            return schemaOrgOffers;
          }

          const { price, currency: priceCurrency, vehicleId } = offer;
          const detailLink = getVehicleDetailPath(typo3, vehicleId);
          if (detailLink) {
            schemaOrgOffers.push(this.schema<Offer>({
              '@context': 'https://schema.org',
              '@type': 'Offer',
              sku: vehicleId,
              price,
              priceCurrency,
              url: `${origin}${detailLink}`,
            }));
          }

          return schemaOrgOffers;
        }, []);

        // todo: add type definitions - this.JsonLd<AutomotiveBusiness>
        // todo: add offers (makesOffer) - https://schema.org/Offer
        const AutomotiveBusiness = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AutomotiveBusiness',
          image: image ? `${origin}${image.imageUri}` : '',
          name,
          legalName: company,
          address,
          geo: {
            '@type': 'GeoCoordinates',
            latitude,
            longitude
          },
          url: `${origin}${location.pathname}`,
          telephone,
          email,
          openingHoursSpecification,
          employee,
          makesOffer
        });

        return (
          <Fragment>
            <script type="application/ld+json">{AutomotiveBusiness}</script>
          </Fragment>
        );
      }
    }

    // magazine page
    if (doktype === 'magazine') {
      const logoImg: string = `${logo}`;
      const img: string = page.magazine_teaser
        ? `${origin}${page.magazine_teaser.imageUri}`
        : logoImg;
      const dateModified = this.formatDate(page.tstamp);
      const datePublished = page.magazine_date
        ? this.formatDate(page.magazine_date)
        : dateModified;

      const Article = this.JsonLd<Article & CreativeWork>({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: `${page.title}`,
        description: `${page.subtitle}`,
        author: {
          '@type': 'Organization',
          name: 'Daimler Buses GmbH'
        },
        image: {
          '@type': 'ImageObject',
          url: `${img}`
        },
        publisher: {
          '@type': 'Organization',
          name: 'BusStore',
          url: `${origin}`,
          logo: {
            '@type': 'ImageObject',
            url: `${logoImg}`
          }
        },
        datePublished: `${datePublished}`,
        dateModified: `${dateModified}`
      });

      return (
        <Fragment>
          <script type="application/ld+json">{Article}</script>
        </Fragment>
      );
    }

    return false;
  }
}

export default withRouter(withSearch(withTypo3(SchemaOrg)));
